import { useState, useEffect } from 'react';
import { 
  Users, Mail, Eye, Trash2, Search, Filter, Download, 
  ChevronDown, ChevronUp, Calendar, CheckCircle, Clock,
  RefreshCw, MessageSquare, Phone, BookOpen, GraduationCap, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [activeTab, setActiveTab] = useState('applications');
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inquiriesRes, contactsRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/applications`)
      ]);
      setInquiries(inquiriesRes.data || []);
      setContacts(contactsRes.data || []);
      setApplications(applicationsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, type) => {
    try {
      await axios.patch(`${API}/${type}/${id}`, { status: 'read' });
      toast.success('Marked as read');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleUpdateApplicationStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { status: newStatus });
      toast.success(`Application marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast.success('Deleted successfully');
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data.length) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]).filter(k => k !== '_id');
    const csvContent = [
      headers.join(','),
      ...data.map(item => 
        headers.map(h => `"${(item[h] || '').toString().replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  const filterData = (data) => {
    return data.filter(item => {
      const matchesSearch = searchQuery === '' || 
        (item.full_name && item.full_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.firstName && item.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.lastName && item.lastName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.email && item.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.subject && item.subject.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.message && item.message.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.programName && item.programName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.applicationNumber && item.applicationNumber.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentData = activeTab === 'inquiries' ? inquiries : activeTab === 'contacts' ? contacts : applications;
  const filteredData = filterData(currentData);

  const stats = {
    totalInquiries: inquiries.length,
    totalContacts: contacts.length,
    totalApplications: applications.length,
    newInquiries: inquiries.filter(i => i.status === 'new' || i.status === 'unread' || !i.status).length,
    newContacts: contacts.filter(c => c.status === 'new' || c.status === 'unread' || !c.status).length,
    pendingApplications: applications.filter(a => a.status === 'pending').length,
  };

  return (
    <div data-testid="admin-page" className="min-h-screen pt-20 bg-[#F5F5F5]">
      {/* Header */}
      <section className="bg-[#0D4A2E] py-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-white/70 mt-1">Manage applications, inquiries and contact submissions</p>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-[#C5A028]">
              <div className="flex items-center gap-3">
                <GraduationCap className="w-6 h-6 text-[#C5A028]" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.totalApplications}</div>
                  <div className="text-xs text-[#666666]">Applications</div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-orange-500">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-orange-500" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.pendingApplications}</div>
                  <div className="text-xs text-[#666666]">Pending</div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-[#0D4A2E]">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-[#0D4A2E]" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.totalInquiries}</div>
                  <div className="text-xs text-[#666666]">Inquiries</div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-green-500">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.newInquiries}</div>
                  <div className="text-xs text-[#666666]">New</div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-blue-500">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-blue-500" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.totalContacts}</div>
                  <div className="text-xs text-[#666666]">Contacts</div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#F5F5F5] border-l-4 border-purple-500">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-purple-500" />
                <div>
                  <div className="text-xl font-bold text-[#0D4A2E]">{stats.newContacts}</div>
                  <div className="text-xs text-[#666666]">New Msgs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* List Panel */}
            <div className="lg:col-span-2">
              {/* Tabs and Controls */}
              <div className="bg-white p-4 border border-[#E5E5E5] mb-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Tabs */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => { setActiveTab('applications'); setSelectedItem(null); }}
                      data-testid="tab-applications"
                      className={`px-4 py-2 font-medium transition-colors duration-200 ${
                        activeTab === 'applications'
                          ? 'bg-[#C5A028] text-[#1A1A1A]'
                          : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#C5A028] hover:text-[#1A1A1A]'
                      }`}
                    >
                      <GraduationCap className="w-4 h-4 inline mr-1" />
                      Applications ({stats.totalApplications})
                    </button>
                    <button
                      onClick={() => { setActiveTab('inquiries'); setSelectedItem(null); }}
                      data-testid="tab-inquiries"
                      className={`px-4 py-2 font-medium transition-colors duration-200 ${
                        activeTab === 'inquiries'
                          ? 'bg-[#0D4A2E] text-white'
                          : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#0D4A2E] hover:text-white'
                      }`}
                    >
                      Inquiries ({stats.totalInquiries})
                    </button>
                    <button
                      onClick={() => { setActiveTab('contacts'); setSelectedItem(null); }}
                      data-testid="tab-contacts"
                      className={`px-4 py-2 font-medium transition-colors duration-200 ${
                        activeTab === 'contacts'
                          ? 'bg-[#0D4A2E] text-white'
                          : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#0D4A2E] hover:text-white'
                      }`}
                    >
                      Contacts ({stats.totalContacts})
                    </button>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={fetchData}
                      data-testid="refresh-btn"
                      className="p-2 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors duration-200"
                      title="Refresh"
                    >
                      <RefreshCw className={`w-5 h-5 text-[#666666] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => exportToCSV(filteredData, activeTab)}
                      data-testid="export-btn"
                      className="p-2 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors duration-200"
                      title="Export CSV"
                    >
                      <Download className="w-5 h-5 text-[#666666]" />
                    </button>
                  </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col md:flex-row gap-4 mt-4">
                  <div className="relative flex-grow">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or message..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="admin-search"
                      className="w-full pl-10 pr-4 py-2 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="status-filter"
                    className="px-4 py-2 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none bg-white"
                  >
                    <option value="all">All Status</option>
                    {activeTab === 'applications' ? (
                      <>
                        <option value="pending">Pending</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                      </>
                    ) : (
                      <>
                        <option value="new">New</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                        <option value="responded">Responded</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* List */}
              <div className="bg-white border border-[#E5E5E5]">
                {loading ? (
                  <div className="p-8 text-center text-[#666666]">Loading...</div>
                ) : filteredData.length === 0 ? (
                  <div className="p-8 text-center text-[#666666]">No {activeTab} found</div>
                ) : (
                  <div className="divide-y divide-[#E5E5E5]">
                    {filteredData.map((item) => (
                      <div
                        key={item.id || item._id}
                        data-testid={`${activeTab}-item-${item.id || item._id}`}
                        className={`p-4 cursor-pointer transition-colors duration-200 ${
                          selectedItem?.id === item.id || selectedItem?._id === item._id
                            ? 'bg-[#F5F5F5] border-l-4 border-[#C5A028]'
                            : 'hover:bg-[#F5F5F5]'
                        } ${(item.status === 'pending' || item.status === 'new' || item.status === 'unread' || !item.status) ? 'border-l-4 border-[#0D4A2E]' : ''}`}
                        onClick={() => setSelectedItem(item)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-[#0D4A2E] truncate">
                                {activeTab === 'applications' ? `${item.firstName} ${item.lastName}` : item.full_name}
                              </span>
                              {item.status === 'pending' && (
                                <span className="px-2 py-0.5 bg-orange-500 text-white text-xs">PENDING</span>
                              )}
                              {item.status === 'reviewing' && (
                                <span className="px-2 py-0.5 bg-blue-500 text-white text-xs">REVIEWING</span>
                              )}
                              {item.status === 'accepted' && (
                                <span className="px-2 py-0.5 bg-green-500 text-white text-xs">ACCEPTED</span>
                              )}
                              {item.status === 'rejected' && (
                                <span className="px-2 py-0.5 bg-red-500 text-white text-xs">REJECTED</span>
                              )}
                              {(!item.status || item.status === 'new' || item.status === 'unread') && activeTab !== 'applications' && (
                                <span className="px-2 py-0.5 bg-[#0D4A2E] text-white text-xs">NEW</span>
                              )}
                            </div>
                            <div className="text-sm text-[#666666] truncate">{item.email}</div>
                            {activeTab === 'applications' && item.programName && (
                              <div className="text-sm text-[#C5A028] mt-1">
                                <GraduationCap className="w-4 h-4 inline mr-1" />
                                {item.programName}
                              </div>
                            )}
                            {activeTab === 'applications' && item.applicationNumber && (
                              <div className="text-xs text-[#999999] mt-1 font-mono">
                                {item.applicationNumber}
                              </div>
                            )}
                            {activeTab === 'inquiries' && item.program_interest && (
                              <div className="text-sm text-[#C5A028] mt-1">
                                <BookOpen className="w-4 h-4 inline mr-1" />
                                {item.program_interest}
                              </div>
                            )}
                            {activeTab === 'contacts' && item.subject && (
                              <div className="text-sm text-[#666666] mt-1 truncate">
                                Subject: {item.subject}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-[#666666] whitespace-nowrap">
                            {formatDate(item.created_at || item.submission_date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-[#E5E5E5] sticky top-28">
                {selectedItem ? (
                  <div>
                    <div className="p-4 bg-[#0D4A2E] text-white">
                      <h3 className="font-semibold">
                        {activeTab === 'applications' ? 'Application Details' : activeTab === 'inquiries' ? 'Inquiry Details' : 'Contact Details'}
                      </h3>
                    </div>
                    
                    {/* Application Details */}
                    {activeTab === 'applications' && (
                      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                        <div className="bg-[#C5A028]/10 p-3 border-l-4 border-[#C5A028]">
                          <p className="font-mono text-sm text-[#666666]">Application #</p>
                          <p className="font-bold text-[#0D4A2E]">{selectedItem.applicationNumber}</p>
                        </div>
                        
                        <div>
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Applicant</label>
                          <p className="font-semibold text-[#0D4A2E]">{selectedItem.firstName} {selectedItem.lastName}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-[#666666] uppercase tracking-wider">Email</label>
                            <p className="text-sm">{selectedItem.email}</p>
                          </div>
                          <div>
                            <label className="text-xs text-[#666666] uppercase tracking-wider">Phone</label>
                            <p className="text-sm">{selectedItem.phone}</p>
                          </div>
                        </div>
                        
                        <div className="border-t pt-4">
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Program</label>
                          <p className="font-semibold text-[#C5A028]">{selectedItem.programName}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-[#666666] uppercase tracking-wider">Track</label>
                            <p className="text-sm">{selectedItem.trackName}</p>
                          </div>
                          <div>
                            <label className="text-xs text-[#666666] uppercase tracking-wider">Intake</label>
                            <p className="text-sm">{selectedItem.intakeName}</p>
                          </div>
                        </div>
                        
                        <div className="border-t pt-4">
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Education</label>
                          <p className="text-sm">{selectedItem.institutionName}</p>
                          <p className="text-xs text-[#666666]">{selectedItem.highestQualification} ({selectedItem.graduationYear})</p>
                        </div>
                        
                        {selectedItem.whyApply && (
                          <div className="border-t pt-4">
                            <label className="text-xs text-[#666666] uppercase tracking-wider">Why Apply</label>
                            <p className="text-sm text-[#666666] mt-1">{selectedItem.whyApply}</p>
                          </div>
                        )}
                        
                        <div className="border-t pt-4">
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Update Status</label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <button
                              onClick={() => handleUpdateApplicationStatus(selectedItem.id, 'reviewing')}
                              className="px-3 py-2 bg-blue-500 text-white text-sm font-medium hover:bg-blue-600"
                            >
                              Reviewing
                            </button>
                            <button
                              onClick={() => handleUpdateApplicationStatus(selectedItem.id, 'accepted')}
                              className="px-3 py-2 bg-green-500 text-white text-sm font-medium hover:bg-green-600"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleUpdateApplicationStatus(selectedItem.id, 'rejected')}
                              className="px-3 py-2 bg-red-500 text-white text-sm font-medium hover:bg-red-600"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => handleDelete(selectedItem.id, 'applications')}
                              className="px-3 py-2 bg-[#666666] text-white text-sm font-medium hover:bg-[#444444]"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Inquiry/Contact Details */}
                    {activeTab !== 'applications' && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="text-xs text-[#666666] uppercase tracking-wider">Name</label>
                        <p className="font-semibold text-[#0D4A2E]">{selectedItem.full_name}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[#666666] uppercase tracking-wider">Email</label>
                        <p className="text-[#0D4A2E]">{selectedItem.email}</p>
                      </div>
                      {selectedItem.phone && (
                        <div>
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Phone</label>
                          <p className="text-[#0D4A2E]">{selectedItem.phone}</p>
                        </div>
                      )}
                      {selectedItem.program_interest && (
                        <div>
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Program Interest</label>
                          <p className="text-[#C5A028] font-medium">{selectedItem.program_interest}</p>
                        </div>
                      )}
                      {selectedItem.subject && (
                        <div>
                          <label className="text-xs text-[#666666] uppercase tracking-wider">Subject</label>
                          <p className="text-[#0D4A2E]">{selectedItem.subject}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-[#666666] uppercase tracking-wider">Message</label>
                        <p className="text-[#0D4A2E] whitespace-pre-wrap bg-[#F5F5F5] p-3 mt-1">
                          {selectedItem.message}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-[#666666] uppercase tracking-wider">Submitted</label>
                        <p className="text-[#0D4A2E]">{formatDate(selectedItem.created_at || selectedItem.submission_date)}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[#666666] uppercase tracking-wider">Status</label>
                        <p className={`font-medium ${
                          selectedItem.status === 'read' ? 'text-blue-600' : 
                          selectedItem.status === 'responded' ? 'text-green-600' : 'text-[#C5A028]'
                        }`}>
                          {selectedItem.status || 'New'}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E5E5E5]">
                        <button
                          onClick={() => handleMarkAsRead(selectedItem.id || selectedItem._id, activeTab)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#0D4A2E] text-white text-sm font-medium hover:bg-[#082D1C] transition-colors duration-200"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark as Read
                        </button>
                        <a
                          href={`mailto:${selectedItem.email}?subject=Re: ${selectedItem.subject || 'Your inquiry to CEPRES'}`}
                          className="flex items-center gap-2 px-4 py-2 bg-[#C5A028] text-[#1A1A1A] text-sm font-medium hover:bg-[#D4B43D] transition-colors duration-200"
                        >
                          <Mail className="w-4 h-4" />
                          Reply
                        </a>
                        <button
                          onClick={() => handleDelete(selectedItem.id || selectedItem._id, activeTab)}
                          className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 text-sm font-medium hover:bg-red-500 hover:text-white transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center text-[#666666]">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Select an item to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
